import React from 'react';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import { NAVIGATION_ITEMS, NAVIGATION_STYLES } from '../../../config/constants';
import { useNavigationPermissions } from '../hooks/useNavigationPermissions';
import { useCustomerLogo } from '../../../hooks/useCustomerLogo';

/**
 * CollapsedNavigation Component
 *
 * Renders the collapsed state of the navigation drawer with only icons and tooltips.
 * Shows a smaller logo, expand button, and icon-only navigation items.
 * Filters items based on user permissions (hides Settings for non-admin users).
 *
 * @param {Object} props - Component props
 * @param {Function} props.onToggle - Callback for toggling navigation
 * @param {Function} props.onItemClick - Callback for navigation item click
 * @param {Function} props.isActive - Function to check if a path is active
 */
const CollapsedNavigation = ({ onToggle, onItemClick, isActive }) => {
  const theme = useTheme();

  // Filter navigation items based on user permissions
  const { visibleItems } = useNavigationPermissions(NAVIGATION_ITEMS);
  const { logoUrl, handleImageError } = useCustomerLogo();

  return (
    <Box
      sx={{
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Logo in collapsed state */}
      <Box
        component="img"
        src={logoUrl}
        alt="Logo"
        sx={{
          height: NAVIGATION_STYLES.logo.collapsed.height,
          width: NAVIGATION_STYLES.logo.collapsed.width,
          mb: 2,
          objectFit: 'contain',
        }}
        onError={handleImageError}
      />

      {/* Expand button */}
      <IconButton
        onClick={onToggle}
        sx={{
          mb: 2,
          backgroundColor:
            theme.palette.grey[200] || theme.palette.background.default,
          '&:hover': {
            backgroundColor: theme.palette.grey[300] || theme.palette.divider,
          },
        }}
      >
        <ChevronLeft sx={{ transform: 'rotate(180deg)' }} />
      </IconButton>

      {/* Navigation items - icons only */}
      {visibleItems.map(item => (
        <Tooltip key={item.id} title={item.label} placement="right">
          <IconButton
            onClick={() => onItemClick(item)}
            sx={{
              mb: 1,
              color: isActive(item.path)
                ? NAVIGATION_STYLES.colors.primary
                : NAVIGATION_STYLES.colors.textSecondary,
              backgroundColor: isActive(item.path)
                ? NAVIGATION_STYLES.colors.selectedBackground
                : 'transparent',
              '&:hover': {
                backgroundColor: isActive(item.path)
                  ? NAVIGATION_STYLES.colors.selectedHover
                  : theme.palette.background.default,
              },
            }}
          >
            {item.icon}
          </IconButton>
        </Tooltip>
      ))}
    </Box>
  );
};

export default CollapsedNavigation;
